import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { useAuth } from "../../Context/AuthContext";
import { ChevronDown, ChevronUp, ExternalLink, Wallet } from "lucide-react";
import {
  EmptyOrders,
  FilterTabs,
  MetricCard,
  OverlayLoader,
  StatusBadge,
  formatPrice,
} from "../shared/OrdersTablePrimitives";
import { openSafeExternalUrl } from "../../utils/security";

const TABS = [
  { id: "all", label: "All" },
  { id: "approved", label: "Completed" },
  { id: "pending", label: "Pending" },
];

const TEST_ORDER_WITHDRAW_CUTOFF = new Date("2026-05-29T00:00:00");

const getOrderTime = (order) => new Date(order.dueDate || order.timestamp || 0).getTime();

const isBeforeTestOrderWithdrawCutoff = (order) => {
  const orderTime = getOrderTime(order);
  return Number.isFinite(orderTime) && orderTime < TEST_ORDER_WITHDRAW_CUTOFF.getTime();
};

const getEditorEarningsAmount = (order) =>
  order?.payout?.editorAmount ?? order?.price?.initial ?? 0;

const EditorOrders = ({ orders, user, setOrders }) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState({});

  const toggleExpand = (orderId) => {
    setExpanded((prev) => ({ ...prev, [orderId]: !prev[orderId] }));
  };

  const flattenedOrders = useMemo(() => {
    if (!orders || Object.keys(orders).length === 0) return [];
    return Object.values(orders)
      .flatMap((statusOrders) =>
        statusOrders ? Object.values(statusOrders) : []
      )
      .filter((order) => order !== undefined)
      .sort((a,b) => {
        const dateA = getOrderTime(a);
        const dateB = getOrderTime(b);
        return dateB-dateA;
      })
  }, [orders]);

  const filteredOrders = useMemo(() => {
    if (filter === "all") return flattenedOrders;
    return flattenedOrders.filter((order) => order.status === filter);
  }, [filter, flattenedOrders]);

  const requestWithdraw = async (order) => {
    if (!user?.connectedAccountId) {
      toast.error(
        "Connected account not found, go to your settings and connect your Stripe account."
      );
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_ENDPOINT}/withdraw-earnings`,
        {
          uid: currentUser.uid,
          orderId: order.orderNumber,
          amount: getEditorEarningsAmount(order),
          connectedAccountId: user.connectedAccountId,
        }
      );
      const transfer = response?.data?.transfer;
      console.log(transfer);
      if (transfer?.onboardingUrl) {
        // 1) Account needs setup
        toast.info(
          "Please finish setting up your Stripe account to receive payouts."
        );
        setTimeout(() => {
          console.log(transfer.onboardingUrl);
          if (!openSafeExternalUrl(transfer.onboardingUrl)) {
            throw new Error("Stripe returned an invalid onboarding link.");
          }
        }, 2000);
      } else if (transfer?.success || response?.data?.success || response?.success) {
        toast.success("Earnings Withdrawn Successfully");
        setOrders((prevOrders) => {
          const nextOrders = { ...prevOrders };

          Object.keys(nextOrders).some((bucketName) => {
            const bucket = nextOrders[bucketName];

            if (!bucket?.[order.orderNumber]) return false;

            nextOrders[bucketName] = {
              ...bucket,
              [order.orderNumber]: {
                ...bucket[order.orderNumber],
                earningsStatus: "paid",
              },
            };

            return true;
          });

          return nextOrders;
        });
      }
    } catch (error) {
      toast.error(error.response?.data || "Error occurred");
    }
    setLoading(false);
  };

  const activeTotal = useMemo(
    () =>
      flattenedOrders
        .filter(
          (order) => order.status === "current" || order.status === "revision"
        )
        .reduce((acc, order) => acc + getEditorEarningsAmount(order), 0),
    [flattenedOrders]
  );

  const completedTotal = useMemo(
    () =>
      flattenedOrders
        .filter(
          (order) => order.status === "approved" || order?.status === "completed"
        )
        .reduce((acc, order) => acc + getEditorEarningsAmount(order), 0),
    [flattenedOrders]
  );

  const openOrder = (order) => navigate(`/order/${order.orderNumber}`);

  const renderEarningsAction = (order) => {
    const isPayable = order.status === "approved" || order?.status === "completed";
    const isTestOrder = isBeforeTestOrderWithdrawCutoff(order);

    if (isPayable) {
      if (isTestOrder) {
        return (
          <button
            type="button"
            className="rounded-xl border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-white/40"
            disabled
          >
            Test Order
          </button>
        );
      }

      return (
        <button
          type="button"
          onClick={() => requestWithdraw(order)}
          className="inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-black disabled:cursor-not-allowed disabled:opacity-50"
          style={{
            background: "linear-gradient(135deg, #10b981, #34d399)",
            boxShadow: "0 10px 24px -12px #10b981",
          }}
          disabled={loading || order?.earningsStatus === "paid"}
        >
          <Wallet size={12} />
          {order?.earningsStatus === "paid" ? "Withdrawn" : "Withdraw"}
        </button>
      );
    }

    return (
      <button
        type="button"
        className="rounded-xl border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-white/40"
        disabled
      >
        Pending
      </button>
    );
  };

  return (
    <div className="space-y-5">
      {loading && <OverlayLoader message="Processing Request" />}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <MetricCard
          label="Active Orders"
          value={formatPrice(activeTotal)}
          accent="purple"
        />
        <MetricCard
          label="Total Completed Orders"
          value={formatPrice(completedTotal)}
          accent="mint"
        />
      </div>

      <FilterTabs tabs={TABS} value={filter} onChange={setFilter} />

      {filteredOrders.length === 0 ? (
        <EmptyOrders />
      ) : (
        <>
          <div className="hidden overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-xl lg:block">
            <div className="grid grid-cols-[1fr_1.8fr_1fr_0.7fr_0.9fr_1fr_1.1fr] gap-3 border-b border-white/10 px-5 py-3 text-[10px] font-semibold uppercase tracking-wider text-white/40">
              <div>Order</div>
              <div>Title</div>
              <div>Due</div>
              <div>Days</div>
              <div>Price</div>
              <div>Status</div>
              <div className="text-right">Earnings</div>
            </div>

            {filteredOrders.map((order, index) => (
              <div
                key={order.orderNumber || index}
                className="grid grid-cols-[1fr_1.8fr_1fr_0.7fr_0.9fr_1fr_1.1fr] items-center gap-3 border-b border-white/5 px-5 py-4 text-sm transition last:border-b-0 hover:bg-white/[0.04]"
              >
                <button
                  type="button"
                  onClick={() => openOrder(order)}
                  className="truncate text-left font-mono text-xs text-white hover:text-emerald-300"
                >
                  #{order.orderNumber}
                </button>

                <div className="min-w-0">
                  <p className="truncate font-semibold text-white">
                    {order.title || "Untitled"}
                  </p>
                  {order.description && (
                    <p className="truncate text-xs text-white/40">
                      {order.description}
                    </p>
                  )}
                </div>

                <div className="text-xs text-white/70">{order.dueDate || "-"}</div>
                <div className="text-xs text-white/70">
                  {order.daysNeeded ?? "-"}
                </div>
                <div className="font-semibold text-white">
                  {formatPrice(order.price?.initial)}
                </div>
                <div>
                  <StatusBadge status={order.status} />
                </div>
                <div className="flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => openOrder(order)}
                    className="inline-flex items-center gap-1.5 rounded-xl border border-white/10 bg-white/5 px-2.5 py-1.5 text-xs font-semibold text-white/80 transition hover:bg-white/10 hover:text-white"
                    aria-label="View order"
                  >
                    <ExternalLink size={12} />
                  </button>
                  {renderEarningsAction(order)}
                </div>
              </div>
            ))}
          </div>

          <div className="space-y-3 lg:hidden">
            {filteredOrders.map((order, index) => {
              const key = order.orderNumber || index;
              const isOpen = expanded[key];

              return (
                <div
                  key={key}
                  className="overflow-hidden rounded-3xl border border-white/10 bg-white/[0.04] p-4 backdrop-blur-xl"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <button
                        type="button"
                        onClick={() => openOrder(order)}
                        className="font-mono text-[11px] text-emerald-300"
                      >
                        #{order.orderNumber}
                      </button>
                      <p className="mt-1 truncate text-sm font-semibold text-white">
                        {order.title || "Untitled"}
                      </p>
                    </div>
                    <StatusBadge status={order.status} />
                  </div>

                  <div className="mt-3 grid grid-cols-3 gap-3 text-xs">
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-white/40">
                        Price
                      </p>
                      <p className="mt-0.5 font-semibold text-white">
                        {formatPrice(order.price?.initial)}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-white/40">
                        Due
                      </p>
                      <p className="mt-0.5 text-white/80">
                        {order.dueDate || "-"}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-white/40">
                        Days
                      </p>
                      <p className="mt-0.5 text-white/80">
                        {order.daysNeeded ?? "-"}
                      </p>
                    </div>
                  </div>

                  {isOpen && order.description && (
                    <p className="mt-3 rounded-xl border border-white/10 bg-black/30 p-3 text-xs leading-relaxed text-white/70">
                      {order.description}
                    </p>
                  )}

                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => openOrder(order)}
                      className="flex-1 rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-white"
                    >
                      View Order
                    </button>
                    {renderEarningsAction(order)}
                    {order.description && (
                      <button
                        type="button"
                        onClick={() => toggleExpand(key)}
                        className="grid h-10 w-10 place-items-center rounded-xl border border-white/10 bg-white/5 text-white/60"
                        aria-label={isOpen ? "Collapse order" : "Expand order"}
                      >
                        {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default React.memo(EditorOrders);
